import {
  MessagePathNode,
  TopicNode,
  pathMatcher as matcher,
} from "@/shared/domain/topics";
import type { Position } from "@/shared/state/visualization/schema/v1/three-d";

import type { TopicDataPathBinding } from "./positionTopicData";
import type { PositionPreset } from "./presets";

/**
 * The 3D position a preset describes, with each axis resolved against the
 * topics of the file its binding names, or undefined when there is no preset.
 * Runs when a user picks a preset for a 3D entity, and when the preset picker
 * checks which preset an entity's current position already matches.
 *
 * An axis holds 0 when the preset never bound it, and when its binding resolves
 * to nothing (its file is not open in the workspace, or carries no such topic
 * or message path).
 */
export function positionFor(
  preset: PositionPreset | undefined,
  fileIdToTopics: Record<string, TopicNode[]>,
): Position | undefined {
  if (!preset) {
    return undefined;
  }
  const forwardMessagePath = messagePathNodeFor(
    preset.bindings?.forward,
    fileIdToTopics,
  );
  const rightMessagePath = messagePathNodeFor(
    preset.bindings?.right,
    fileIdToTopics,
  );
  const upMessagePath = messagePathNodeFor(preset.bindings?.up, fileIdToTopics);
  return {
    transformation: preset.transformation,
    basePreset: preset.basePreset,
    forward: forwardMessagePath?.toTopicData() ?? 0,
    forwardScale: preset.scales.forward,
    right: rightMessagePath?.toTopicData() ?? 0,
    rightScale: preset.scales.right,
    up: upMessagePath?.toTopicData() ?? 0,
    upScale: preset.scales.up,
  };
}

function messagePathNodeFor(
  binding: TopicDataPathBinding | undefined,
  fileIdToTopics: Record<string, TopicNode[]>,
): MessagePathNode | undefined {
  if (binding === undefined) {
    return undefined;
  }

  // A topic name is unique within one file, and a list of parts identifies at
  // most one node within a topic, so the first match is the only match.
  return matcher
    .findNodesByParts(
      binding.topicName,
      binding.messagePath,
      fileIdToTopics[binding.fileId] ?? [],
    )
    .at(0);
}
